import { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Plus,
  ChevronLeft,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  Clock,
  User,
  Calendar,
  Download,
  Copy,
  Trash2,
  Edit,
  MoreVertical,
  CheckCircle2,
  Circle,
  AlertCircle,
  Loader2,
  Sparkles,
  FileUp,
  Pencil,
  Check,
  X,
} from "lucide-react";
import {
  useProposals,
  useCreateProposal,
  useDeleteProposal,
  useUpdateProposalById,
  useProposalTemplates,
  type ProposalListItem,
  type ProposalType,
  type ProposalStatus,
  type ProposalTemplate,
} from "@/hooks/use-proposals";
import { showToast } from "@/lib/toast";
import { fetchWithAuth, fetchBlobWithAuth } from "@/lib/api/client";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SuggestStructureDialog,
  type StructureSource,
} from "@/components/proposals/suggest-structure-dialog";

// Types
interface Licitacion {
  id: string;
  titulo: string;
  organismo: string;
  estado: string;
}

// Status / Type configuration alineado con los enums del backend
// (`backend/app/models/proposal.py`).
const statusConfig: Record<
  ProposalStatus,
  { label: string; color: string; bg: string; icon: React.ComponentType<{ className?: string }> }
> = {
  BORRADOR: { label: "Borrador", color: "text-gray-700", bg: "bg-gray-100", icon: Circle },
  EN_REVISION: { label: "En Revisión", color: "text-blue-700", bg: "bg-blue-100", icon: Clock },
  APROBADA: { label: "Aprobada", color: "text-green-700", bg: "bg-green-100", icon: CheckCircle2 },
  ENVIADA: { label: "Enviada", color: "text-purple-700", bg: "bg-purple-100", icon: CheckCircle2 },
};

const defaultStatusInfo = { label: "Desconocido", color: "text-gray-500", bg: "bg-gray-50", icon: Circle };

const typeConfig: Record<
  ProposalType,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  TECNICA: { label: "Técnica", icon: FileText, color: "text-blue-600" },
  ECONOMICA: { label: "Económica", icon: FileSpreadsheet, color: "text-green-600" },
  COMPLETA: { label: "Completa", icon: FileText, color: "text-orange-600" },
};

const defaultTypeInfo = { label: "Propuesta", icon: FileText, color: "text-gray-600" };

export default function ProposalsListPage() {
  const { id } = useParams<{ id: string }>();
  const licitacionId = id!;
  const navigate = useNavigate();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  // Data fetching con React Query
  const {
    data: licitacion,
    isLoading: licitacionLoading,
    error: licitacionError,
  } = useQuery<Licitacion>({
    queryKey: ["licitacion", licitacionId],
    queryFn: () => fetchWithAuth(`/api/v1/licitaciones/${licitacionId}`),
    enabled: !!licitacionId,
  });

  const {
    data: proposals = [],
    isLoading: proposalsLoading,
    error: proposalsError,
  } = useProposals(licitacionId);

  // Mutations
  const createProposal = useCreateProposal();
  const deleteProposal = useDeleteProposal();

  // Mejoras L/I (changelog cliente): selector de plantilla para crear
  // propuestas con secciones predefinidas. Si no hay plantillas, el botón
  // "Nueva Propuesta" cae al comportamiento clásico (sin template_id).
  const { data: templates = [] } = useProposalTemplates();
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const templateMenuRef = useRef<HTMLDivElement | null>(null);

  // Mejora "Sugerir estructura" (changelog cliente): diálogo que pide a
  // la IA una estructura basada en el pliego de esta licitación.
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestType, setSuggestType] = useState<ProposalType>("TECNICA");
  // De dónde sale la estructura: sugerida por IA o importada de un Word.
  const [suggestSource, setSuggestSource] = useState<StructureSource>("ai");

  useEffect(() => {
    if (!templateMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(e.target as Node)
      ) {
        setTemplateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [templateMenuOpen]);

  // Para exportar necesitamos un hook por propuesta, así que usamos un estado para trackear
  const [exportingId, setExportingId] = useState<string | null>(null);

  // Edición rápida desde la tarjeta: título inline + estado desplegable.
  const updateProposalMutation = useUpdateProposalById();
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);

  const startEditTitle = (p: ProposalListItem) => {
    setEditingTitleId(p.id);
    setTitleDraft(p.title);
  };
  const saveTitle = (p: ProposalListItem) => {
    const t = titleDraft.trim();
    if (t && t !== p.title) {
      updateProposalMutation.mutate({ proposalId: p.id, data: { title: t } });
    }
    setEditingTitleId(null);
  };
  const changeStatus = (p: ProposalListItem, status: ProposalStatus) => {
    if (status !== p.status) {
      updateProposalMutation.mutate({ proposalId: p.id, data: { status } });
    }
    setStatusMenuId(null);
  };

  const handleCreateProposal = useCallback(
    (proposalType: ProposalType = "TECNICA", template?: ProposalTemplate) => {
      createProposal.mutate(
        {
          licitacion_id: licitacionId,
          proposal_type: template?.template_type ?? proposalType,
          template_id: template?.id,
          // Si la propuesta nace de una plantilla, usamos su nombre como
          // título inicial para que el usuario sepa de qué plantilla salió.
          ...(template ? { title: template.name } : {}),
        },
        {
          onSuccess: (data) => {
            showToast.success("Propuesta creada correctamente");
            navigate(`/licitaciones/${licitacionId}/propuestas/${data.id}`);
          },
          onError: (error) => {
            showToast.error(`Error al crear propuesta: ${error.message}`);
          },
        }
      );
      setTemplateMenuOpen(false);
    },
    [createProposal, licitacionId, navigate]
  );

  const handleEditProposal = useCallback(
    (proposalId: string) => {
      navigate(`/licitaciones/${licitacionId}/propuestas/${proposalId}`);
    },
    [licitacionId, navigate]
  );

  const handleDuplicateProposal = useCallback(
    (proposal: ProposalListItem) => {
      createProposal.mutate(
        {
          licitacion_id: licitacionId,
          proposal_type: proposal.proposal_type,
          title: `${proposal.title} (copia)`,
        },
        {
          onSuccess: () => {
            showToast.success("Propuesta duplicada correctamente");
          },
          onError: (error) => {
            showToast.error(`Error al duplicar propuesta: ${error.message}`);
          },
        }
      );
    },
    [createProposal, licitacionId]
  );

  const handleDeleteProposal = useCallback(
    (proposalId: string) => {
      const confirmed = window.confirm(
        "¿Estás seguro de que deseas eliminar esta propuesta? Esta acción no se puede deshacer."
      );
      if (!confirmed) return;

      deleteProposal.mutate(
        { proposalId, licitacionId },
        {
          onSuccess: () => {
            showToast.success("Propuesta eliminada correctamente");
          },
          onError: (error) => {
            showToast.error(`Error al eliminar propuesta: ${error.message}`);
          },
        }
      );
    },
    [deleteProposal, licitacionId]
  );

  const handleExport = useCallback(
    async (proposalId: string, format: "docx" | "pdf") => {
      setExportingId(proposalId);
      try {
        const blob = await fetchBlobWithAuth(
          `/api/v1/proposals/${proposalId}/export/${format}`
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `propuesta-${proposalId}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast.success(`Propuesta exportada como ${format.toUpperCase()}`);
      } catch (error) {
        showToast.error(
          `Error al exportar: ${error instanceof Error ? error.message : "Error desconocido"}`
        );
      } finally {
        setExportingId(null);
      }
    },
    []
  );

  const loading = licitacionLoading || proposalsLoading;
  const error = licitacionError || proposalsError;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mb-6">
          <Skeleton className="h-5 w-40 mb-4" />
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-9 w-64" />
              <Skeleton className="h-5 w-80" />
            </div>
            <Skeleton className="h-10 w-44" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-gray-200 p-6 space-y-4"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-6 w-6 rounded" />
              </div>
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-2 w-full rounded-full" />
              <div className="flex items-center justify-between pt-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !licitacion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error al cargar</h2>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "Licitación no encontrada"}
          </p>
          <button
            onClick={() => navigate(`/licitaciones/${licitacionId}`)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Volver a la licitación
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/licitaciones/${licitacionId}`)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          <span>Volver a la licitación</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Propuestas</h1>
            <p className="text-gray-600">{licitacion.titulo}</p>
          </div>
          {/* Mejoras L/I (changelog cliente): dropdown con plantillas
              disponibles + opción "En blanco". Si no hay plantillas se
              comporta como antes (un botón directo). */}
          <div className="relative" ref={templateMenuRef}>
            <button
              onClick={() => setTemplateMenuOpen((v) => !v)}
              disabled={createProposal.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createProposal.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
              <span>Nueva Propuesta</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {templateMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                {/* Mejora "Sugerir estructura": opción IA al principio */}
                <button
                  type="button"
                  onClick={() => {
                    setTemplateMenuOpen(false);
                    setSuggestType("TECNICA");
                    setSuggestSource("ai");
                    setSuggestOpen(true);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-purple-50 transition-colors border-b border-gray-100"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-800">
                      Sugerir estructura desde el pliego con IA
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 ml-5">
                    Lee las bases de la convocatoria y propone secciones.
                  </div>
                </button>

                {/* Importar el índice desde un Word: el pliego suele imponer
                    una estructura exacta que el cliente ya tiene en un .docx. */}
                <button
                  type="button"
                  onClick={() => {
                    setTemplateMenuOpen(false);
                    setSuggestType("TECNICA");
                    setSuggestSource("file");
                    setSuggestOpen(true);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100"
                >
                  <div className="flex items-center gap-2">
                    <FileUp className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-800">
                      Importar índice desde Word
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 ml-5">
                    Sube un .docx y crea las secciones con su índice.
                  </div>
                </button>

                {templates.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      Crear desde plantilla
                    </div>
                    <ul className="py-1 max-h-72 overflow-y-auto">
                      {templates.map((template) => (
                        <li key={template.id}>
                          <button
                            type="button"
                            onClick={() =>
                              handleCreateProposal(template.template_type, template)
                            }
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {template.name}
                              </span>
                              {template.is_default && (
                                <span className="ml-auto text-[10px] text-amber-700">
                                  Por defecto
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {template.sections_config.length}{" "}
                              {template.sections_config.length === 1
                                ? "sección"
                                : "secciones"}{" "}
                              · {template.template_type}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => handleCreateProposal("TECNICA")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors text-sm text-gray-700"
                  >
                    Propuesta en blanco (sin plantilla)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Proposals List */}
      {proposals.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No hay propuestas aún</h3>
          <p className="text-gray-600 mb-6">
            Comienza creando una propuesta técnica o económica para esta licitación
          </p>
          <button
            onClick={() => handleCreateProposal("TECNICA")}
            disabled={createProposal.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createProposal.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
            <span>Crear Primera Propuesta</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {proposals.map((proposal) => {
            const typeInfo = typeConfig[proposal.proposal_type] || defaultTypeInfo;
            const statusInfo = statusConfig[proposal.status] || defaultStatusInfo;
            const TypeIcon = typeInfo.icon;
            const StatusIcon = statusInfo.icon;
            const completionPercentage = proposal.stats?.completion_percentage ?? 0;
            const sectionsCompleted = proposal.stats?.sections_completed ?? 0;
            const totalSections = proposal.stats?.total_sections ?? 0;
            const isExporting = exportingId === proposal.id;
            const isDeleting = deleteProposal.isPending;

            return (
              <div
                key={proposal.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                    <span className="text-sm font-medium text-gray-600">{typeInfo.label}</span>
                  </div>
                  <div className="relative" ref={openMenuId === proposal.id ? menuRef : undefined}>
                    <button
                      onClick={() => setOpenMenuId(openMenuId === proposal.id ? null : proposal.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>
                    {openMenuId === proposal.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        <button
                          onClick={() => {
                            handleEditProposal(proposal.id);
                            setOpenMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Editar</span>
                        </button>
                        <button
                          onClick={() => {
                            handleExport(proposal.id, "docx");
                            setOpenMenuId(null);
                          }}
                          disabled={isExporting}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {isExporting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <span>Exportar DOCX</span>
                        </button>
                        <button
                          onClick={() => {
                            handleExport(proposal.id, "pdf");
                            setOpenMenuId(null);
                          }}
                          disabled={isExporting}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {isExporting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <span>Exportar PDF</span>
                        </button>
                        <button
                          onClick={() => {
                            handleDuplicateProposal(proposal);
                            setOpenMenuId(null);
                          }}
                          disabled={createProposal.isPending}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {createProposal.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                          <span>Duplicar</span>
                        </button>
                        <button
                          onClick={() => {
                            handleDeleteProposal(proposal.id);
                            setOpenMenuId(null);
                          }}
                          disabled={isDeleting}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {isDeleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          <span>Eliminar</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Title — editable inline */}
                {editingTitleId === proposal.id ? (
                  <div className="flex items-center gap-1 mb-2">
                    <input
                      autoFocus
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveTitle(proposal);
                        if (e.key === "Escape") setEditingTitleId(null);
                      }}
                      className="flex-1 px-2 py-1 text-lg font-semibold border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <button
                      type="button"
                      onClick={() => saveTitle(proposal)}
                      className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                      aria-label="Guardar título"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingTitleId(null)}
                      className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                      aria-label="Cancelar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2 mb-2 group/title">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {proposal.title}
                    </h3>
                    <button
                      type="button"
                      onClick={() => startEditTitle(proposal)}
                      className="p-1 text-gray-300 hover:text-gray-600 opacity-0 group-hover/title:opacity-100 transition-opacity flex-shrink-0"
                      aria-label="Renombrar propuesta"
                      title="Renombrar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Status — desplegable para cambiarlo */}
                <div className="relative inline-block mb-4">
                  <button
                    type="button"
                    onClick={() =>
                      setStatusMenuId(
                        statusMenuId === proposal.id ? null : proposal.id
                      )
                    }
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${statusInfo.bg} hover:brightness-95 transition`}
                    title="Cambiar estado"
                  >
                    <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
                    <span className={`text-sm font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 ${statusInfo.color}`} />
                  </button>
                  {statusMenuId === proposal.id && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setStatusMenuId(null)}
                      />
                      <div className="absolute left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                        {(Object.keys(statusConfig) as ProposalStatus[]).map(
                          (st) => {
                            const cfg = statusConfig[st];
                            const Ico = cfg.icon;
                            return (
                              <button
                                key={st}
                                type="button"
                                onClick={() => changeStatus(proposal, st)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                                  st === proposal.status ? "font-semibold" : ""
                                }`}
                              >
                                <Ico className={`w-4 h-4 ${cfg.color}`} />
                                <span>{cfg.label}</span>
                                {st === proposal.status && (
                                  <Check className="w-3.5 h-3.5 ml-auto text-gray-400" />
                                )}
                              </button>
                            );
                          }
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Progreso</span>
                    <span className="font-medium">
                      {sectionsCompleted}/{totalSections} secciones
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${completionPercentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>Versión {proposal.version}</span>
                  </div>
                  {proposal.created_by_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Creada por {proposal.created_by_name}</span>
                    </div>
                  )}
                  {proposal.due_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Vence:{" "}
                        {new Date(proposal.due_date).toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>
                      Actualizado:{" "}
                      {new Date(proposal.updated_at).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleEditProposal(proposal.id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    <span className="font-medium">Editar Propuesta</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SuggestStructureDialog
        isOpen={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        licitacionId={licitacionId}
        proposalType={suggestType}
        source={suggestSource}
        onCreated={(newProposalId) => {
          navigate(`/licitaciones/${licitacionId}/propuestas/${newProposalId}`);
        }}
      />
    </div>
  );
}
